// src/components/Leader/IndividualContributorPage.tsx
import React, { useMemo, useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { HbrSpinner } from '@harbor/elements-react-standalone';

import LeaderTileStrip from './LeaderTileStrip';
import IndividualContributorOpportunitiesTable from './Salesforce/IndividualContributorOppsTable';
import StbAccordion from '../security-toolbox-ui/StbAccordion';

import { useFetchDashboardPlacements, useFetchDashboardTileLinks } from '../../hooks/toolbox-hooks';
import type { DashboardPlacement, DashboardTileLink } from '../../types/dashboard';
import { TileRuntimeContext } from '../../support/tileRuntimeContext';
import { retitleForPeriod } from '../../support/fiscalTimePeriods';

interface Props {
  email: string;
  /** `all` or `range:YYYY-MM-DD:YYYY-MM-DD` from the Salesforce time dropdown. */
  timeFilter?: string;
  /** Short name of the period for tile titles (e.g. "This Fiscal Quarter"). */
  timeShort?: string;
  /** The fiscal calendar is still loading. */
  timePending?: boolean;
  enableDebugLogging?: boolean;
}

const COMPONENT_KEY_IC = 'Leader.IndividualContributorPage';
const REGION_KEY_SCORECARD = 'scorecard';

export default function IndividualContributorPage({
                                                    email,
                                                    timeFilter = 'all',
                                                    timeShort,
                                                    timePending = false,
                                                    enableDebugLogging = false,
                                                  }: Props) {
  const [isScorecardOpen, setIsScorecardOpen] = useState(true);

  const {
    data: placements,
    isLoading: isLoadingPlacements,
    error: placementsError,
  } = useFetchDashboardPlacements(
    { component_key: COMPONENT_KEY_IC, region_key: REGION_KEY_SCORECARD, enabled: true },
    { placeholderData: keepPreviousData, refetchOnWindowFocus: false },
  );

  const placement: DashboardPlacement | null = useMemo(() => {
    const arr = Array.isArray(placements) ? placements : [];
    return arr.length ? arr[0] : null;
  }, [placements]);

  const {
    data: tileLinks,
    isLoading: isLoadingLinks,
    error: linksError,
  } = useFetchDashboardTileLinks(
    placement?.id ? { placement: placement.id, enabled: true, order_by: 'order' } : undefined,
    { enabled: !!placement?.id, placeholderData: keepPreviousData, refetchOnWindowFocus: false },
  );

  const runtimeContext: TileRuntimeContext = useMemo(() => {
    const context: TileRuntimeContext = {
      email,
      userEmail: email,
      ownerEmail: email,
      domain: 'salesforce' as const,
      teamScope: [email],
      teamEmailsList: email,
      teamSize: 1,
      viewMode: 'individual' as const,
      isTeamView: false,
      isIndividualView: true,
      perspective: 'individual_contributor' as const,
      current_time_filter: timeFilter,
    };

    if (enableDebugLogging) {
      console.log('IndividualContributorPage: Runtime Context', context);
    }

    return context;
  }, [email, enableDebugLogging, timeFilter]);

  if (placementsError) {
    return <div style={{ padding: 12, color: 'red' }}>{(placementsError as Error).message}</div>;
  }
  if (linksError) {
    return <div style={{ padding: 12, color: 'red' }}>{(linksError as Error).message}</div>;
  }

  const columns = 1;

  if (enableDebugLogging) {
    console.log('IndividualContributorPage: Tile Links', {
      placement,
      tileLinks,
      linksCount: Array.isArray(tileLinks) ? tileLinks.length : 0,
    });
  }

  return (
    <div className="flex flex-col gap-4 h-full min-h-0 min-w-0">
      <StbAccordion
        variant="minimal"
        label="SE Scorecard"
        isOpen={isScorecardOpen}
        toggleIsOpen={setIsScorecardOpen}
      >
        <div
          className="flex flex-col gap-3"
          style={{
            height: 350,
            minHeight: 0,
            minWidth: 0,
            padding: 12,
            overflow: 'hidden',
          }}
        >
          {isLoadingPlacements || isLoadingLinks || timePending ? (
            <HbrSpinner>Loading scorecard…</HbrSpinner>
          ) : !placement || !tileLinks || (Array.isArray(tileLinks) && tileLinks.length === 0) ? (
            <div className="hbr-css__text-secondary-sm">
              No dashboard tiles configured for {COMPONENT_KEY_IC}/{REGION_KEY_SCORECARD}.
            </div>
          ) : (
            <div
              className="grid gap-3"
              style={{
                height: '100%',
                minHeight: 0,
                minWidth: 0,
                gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
              }}
            >
              {(Array.isArray(tileLinks) ? (tileLinks as DashboardTileLink[]) : [])
                .slice()
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id - b.id)
                .map((link) => {
                  const tileName = link?.tile?.name;
                  if (!tileName) return null;

                  const colSpan = Number((link.options as any)?.colSpan ?? 1);
                  const minHeight = (link.options as any)?.minHeight ?? 0;

                  if (enableDebugLogging) {
                    console.log(`IndividualContributorPage: Rendering tile ${tileName}`, {
                      link,
                      colSpan,
                      minHeight,
                      tileSettings: link.tile?.settings,
                    });
                  }

                  return (
                    <div
                      key={`scorecard-tile-${link.id}`}
                      className="min-w-0 flex flex-col"
                      style={{
                        minHeight,
                        height: '100%',
                        minWidth: 0,
                        gridColumn: `span ${Math.max(1, Math.min(columns, colSpan))}`,
                      }}
                    >
                      <LeaderTileStrip
                        tileNames={[tileName]}
                        runtimeContext={runtimeContext}
                        className="h-full min-w-0"
                        enableDebugLogging={enableDebugLogging}
                        currentTimeFilter={timeFilter}
                        titleTransform={(name) => retitleForPeriod(name, timeShort)}
                      />
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </StbAccordion>

      <div
        className="hbr-css__container-card p-3 w-full"
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          className="hbr-css__text-primary-sm-semibold mb-2"
          style={{ flex: '0 0 auto', minWidth: 0 }}
        >
          Opportunities (Individual Perspective)
        </div>

        <div
          style={{
            flex: '1 1 auto',
            minHeight: 0,
            minWidth: 0,
            overflow: 'auto',
          }}
        >
          {timePending ? (
            <HbrSpinner>Loading fiscal calendar…</HbrSpinner>
          ) : (
            <IndividualContributorOpportunitiesTable email={email} timeFilter={timeFilter} />
          )}
        </div>
      </div>
    </div>
  );
}